import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import DashboardLayout from "@/components/DashboardLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Save, Clock, Euro, CalendarOff, Plus, X, Lock, Zap } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { format, addDays, isWeekend } from "date-fns";
import { it } from "date-fns/locale";
import { PriceListPanel } from "@/pages/PriceListPage";

function TempistichePanel() {
  const [options, setOptions] = useState(null);
  const [types, setTypes] = useState([]);
  const [byType, setByType] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [urgencyEnabled, setUrgencyEnabled] = useState(true);

  useEffect(() => {
    api.get("/order-options").then(({ data }) => {
      setOptions(data);
      const wt = [...new Set([...(data.materials || []), ...(data.design_types || [])].filter(Boolean))].sort((a, b) => a.localeCompare(b));
      setTypes(wt);
      setByType(data.deadline_by_type || {});
      setUrgencyEnabled(data.urgency_enabled !== false);
    }).catch(() => toast.error("Errore nel caricamento")).finally(() => setLoading(false));
  }, []);

  const setVal = (type, cat, val) => {
    const n = val === "" ? "" : Math.max(0, parseInt(val, 10) || 0);
    setByType((prev) => ({ ...prev, [type]: { ...(prev[type] || {}), [cat]: n } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = {};
      types.forEach((t) => {
        const e = byType[t] || {};
        cleaned[t] = {
          laboratorio: parseInt(e.laboratorio, 10) || 0,
          studio: parseInt(e.studio, 10) || 0,
        };
      });
      await api.put("/order-options", { options: { ...options, deadline_by_type: cleaned, urgency_enabled: urgencyEnabled } });
      setOptions((o) => ({ ...o, urgency_enabled: urgencyEnabled }));
      toast.success("Tempistiche salvate");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;

  return (
    <div className="space-y-4">
      <Card data-testid="urgency-setting-card" className={`border p-4 flex items-center justify-between gap-4 ${urgencyEnabled ? "border-amber-200 bg-amber-50/60" : "border-slate-200 bg-slate-50"}`}>
        <div className="flex items-start gap-3">
          <Zap className={`h-5 w-5 mt-0.5 shrink-0 ${urgencyEnabled ? "text-amber-600" : "text-slate-400"}`} />
          <div>
            <p className="text-sm font-semibold text-slate-800">Tasto "Richiedi urgenza" per i clienti</p>
            <p className="text-xs text-slate-500">{urgencyEnabled ? "Attivo: i clienti possono richiedere l'urgenza e scegliere date ravvicinate." : "Disattivato (overload): i clienti non vedono l'opzione urgenza e devono rispettare i tempi minimi."} Ricordati di salvare.</p>
          </div>
        </div>
        <Switch data-testid="urgency-enabled-toggle" checked={urgencyEnabled} onCheckedChange={(v) => setUrgencyEnabled(!!v)} />
      </Card>
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500 flex items-center gap-2"><Clock className="h-4 w-4 text-teal-600" /> Giorni lavorativi minimi prima della scadenza, per tipo di lavoro e categoria cliente.</p>
        <Button data-testid="save-tempistiche" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva tempistiche
        </Button>
      </div>
      <Card className="border-slate-200 overflow-x-auto">
        <Table data-testid="tempistiche-table">
          <TableHeader>
            <TableRow>
              <TableHead>Lavorazione</TableHead>
              <TableHead className="w-48">Laboratorio (gg lav.)</TableHead>
              <TableHead className="w-48">Studio dentistico (gg lav.)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {types.map((t) => (
              <TableRow key={t} data-testid="tempistica-row">
                <TableCell className="font-medium text-slate-800">{t}</TableCell>
                <TableCell>
                  <Input data-testid={`tempo-lab-${t}`} type="number" min="0" value={(byType[t] || {}).laboratorio ?? ""} onChange={(e) => setVal(t, "laboratorio", e.target.value)} className="w-28" placeholder="0" />
                </TableCell>
                <TableCell>
                  <Input data-testid={`tempo-studio-${t}`} type="number" min="0" value={(byType[t] || {}).studio ?? ""} onChange={(e) => setVal(t, "studio", e.target.value)} className="w-28" placeholder="0" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <p className="text-xs text-slate-400">Se una lavorazione resta a 0 per una categoria, verrà usata la regola generale esistente. Il cliente non può scegliere una scadenza più ravvicinata, ma può usare "Richiedi urgenza".</p>
    </div>
  );
}

function OperatorPricesPanel() {
  const [options, setOptions] = useState(null);
  const [rules, setRules] = useState([]);
  const [def, setDef] = useState(15);
  const [torontoEl, setTorontoEl] = useState(12);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/order-options").then(({ data }) => {
      setOptions(data);
      setRules(data.operator_prices || [
        { keyword: "scomposizione", label: "Scomposizione", price: 50, per_element: false },
        { keyword: "model creator", label: "Model creator", price: 10, per_element: true },
        { keyword: "provvisorio", label: "Provvisorio", price: 5, per_element: true },
      ]);
      setDef(data.operator_default_price ?? 15);
      setTorontoEl(data.operator_toronto_elements ?? 12);
    }).catch(() => toast.error("Errore nel caricamento"));
  }, []);

  const upd = (i, k, v) => setRules((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const save = async () => {
    setSaving(true);
    try {
      const cleaned = rules.filter((r) => (r.keyword || "").trim()).map((r) => ({ keyword: r.keyword.trim(), label: (r.label || r.keyword).trim(), price: parseFloat(r.price) || 0, per_element: !!r.per_element }));
      await api.put("/order-options", { options: { ...options, operator_prices: cleaned, operator_default_price: parseFloat(def) || 0, operator_toronto_elements: parseInt(torontoEl, 10) || 12 } });
      setRules(cleaned);
      toast.success("Listino collega salvato");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (!options) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-slate-600 rounded-lg bg-white/80 border border-slate-200 px-3 py-2">Tariffe usate nel <strong>Report collega</strong>. Ogni lavorazione il cui nome contiene la parola chiave prende quella tariffa; tutte le altre valgono come <strong>modellazione</strong> alla tariffa di default.</p>
        <Button data-testid="save-operator-prices" onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 shrink-0">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva listino collega
        </Button>
      </div>
      <Card className="border-slate-200 p-4 flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-slate-800">Modellazione (default, per elemento)</span>
        <Input data-testid="operator-default-price" type="number" step="0.5" min="0" value={def} onChange={(e) => setDef(e.target.value)} className="w-28" />
        <span className="text-sm text-slate-500">€</span>
        <span className="mx-2 h-6 w-px bg-slate-200" />
        <span className="text-sm font-medium text-slate-800">Toronto / barra: elementi se non indicati</span>
        <Input data-testid="operator-toronto-elements" type="number" min="1" value={torontoEl} onChange={(e) => setTorontoEl(e.target.value)} className="w-24" />
        <span className="text-xs text-slate-500">Una Toronto/barra conta Modellazione × elementi (quantità dell'ordine, oppure questo valore) + 1 Scomposizione.</span>
      </Card>
      <Card className="border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Parola chiave (nel nome lavorazione)</TableHead><TableHead>Etichetta</TableHead><TableHead className="w-32">Tariffa €</TableHead><TableHead className="w-40">Conteggio</TableHead><TableHead className="w-12"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rules.map((r, i) => (
              <TableRow key={i} data-testid="operator-price-row">
                <TableCell><Input value={r.keyword || ""} onChange={(e) => upd(i, "keyword", e.target.value)} placeholder="es. toronto" /></TableCell>
                <TableCell><Input value={r.label || ""} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Toronto" /></TableCell>
                <TableCell><Input type="number" step="0.5" min="0" value={r.price} onChange={(e) => upd(i, "price", e.target.value)} /></TableCell>
                <TableCell>
                  <select value={r.per_element ? "el" : "case"} onChange={(e) => upd(i, "per_element", e.target.value === "el")} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                    <option value="el">per elemento</option><option value="case">a corpo (per caso)</option>
                  </select>
                </TableCell>
                <TableCell><button type="button" onClick={() => setRules((x) => x.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="p-3 border-t border-slate-100">
          <Button data-testid="add-operator-price" type="button" variant="outline" size="sm" onClick={() => setRules((x) => [...x, { keyword: "", label: "", price: 0, per_element: true }])}><Plus className="h-4 w-4 mr-1.5" /> Aggiungi voce</Button>
        </div>
      </Card>
    </div>
  );
}

function ReportSecurityPanel() {
  const [isSet, setIsSet] = useState(null);
  const [cur, setCur] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/reports/security").then(({ data }) => setIsSet(data.is_set)).catch(() => setIsSet(false)); }, []);

  const save = async () => {
    if (pwd.length < 4) return toast.error("Almeno 4 caratteri");
    if (pwd !== pwd2) return toast.error("Le due password non coincidono");
    setSaving(true);
    try {
      await api.put("/reports/security", { new_password: pwd, current_password: isSet ? cur : null });
      toast.success("Password report salvata");
      setIsSet(true); setCur(""); setPwd(""); setPwd2("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (isSet === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;
  return (
    <Card className="border-slate-200 p-5 max-w-lg space-y-4">
      <div className="flex items-start gap-2 text-sm text-slate-600"><Lock className="h-4 w-4 text-teal-600 mt-0.5 shrink-0" /> Password aggiuntiva richiesta per scaricare il <strong>Report clienti</strong> e il <strong>Report collega</strong>. Stato: <span data-testid="report-password-status" className={`font-semibold ${isSet ? "text-teal-700" : "text-red-600"}`}>{isSet ? "impostata" : "non impostata"}</span></div>
      {isSet && <Input data-testid="report-current-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Password attuale" />}
      <Input data-testid="report-new-password" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder={isSet ? "Nuova password" : "Password report"} />
      <Input data-testid="report-new-password-2" type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} placeholder="Ripeti password" />
      <Button data-testid="save-report-password" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
        {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} {isSet ? "Cambia password" : "Imposta password"}
      </Button>
    </Card>
  );
}

function ChiusurePanel() {
  const [options, setOptions] = useState(null);
  const [days, setDays] = useState([]);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/order-options").then(({ data }) => { setOptions(data); setDays([...(data.closure_days || [])].sort()); })
      .catch(() => toast.error("Errore nel caricamento"));
  }, []);

  const key = (d) => format(d, "yyyy-MM-dd");
  const addRange = () => {
    if (!range.from) return;
    const end = range.to || range.from;
    const span = Math.round((end - range.from) / 86400000) + 1;
    if (span > 21 && !window.confirm(`Stai per inserire una chiusura di ${span} giorni consecutivi: i clienti non potranno scegliere nessuna di queste date, nemmeno con urgenza. Confermi?`)) return;
    const out = new Set(days);
    let d = range.from;
    while (d <= end) { if (!isWeekend(d)) out.add(key(d)); d = addDays(d, 1); }
    setDays([...out].sort());
    setRange({ from: undefined, to: undefined });
  };
  const remove = (k) => setDays((prev) => prev.filter((x) => x !== k));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/order-options", { options: { ...options, closure_days: days } });
      toast.success("Chiusure salvate");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (!options) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;
  const fmt = (k) => format(new Date(k + "T00:00:00"), "EEE d MMM yyyy", { locale: it });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-slate-600 flex items-start gap-2 rounded-lg bg-white/80 border border-slate-200 px-3 py-2"><CalendarOff className="h-4 w-4 text-teal-600 mt-0.5 shrink-0" /> Giorni di chiusura del laboratorio (ferie, ponti). Vengono esclusi dal calendario scadenze e dal conteggio dei giorni lavorativi, come weekend e festività nazionali.</p>
        <Button data-testid="save-chiusure" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 shrink-0">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva chiusure
        </Button>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-slate-200 p-4 space-y-3">
          <p className="text-sm font-medium text-slate-800">Seleziona un giorno o un periodo</p>
          <Calendar mode="range" selected={range} onSelect={(r) => setRange(r || { from: undefined, to: undefined })} disabled={(d) => isWeekend(d)} locale={it} />
          <Button data-testid="add-chiusura" variant="outline" onClick={addRange} disabled={!range.from} className="w-full">
            <Plus className="h-4 w-4 mr-1.5" /> Aggiungi {range.from ? (range.to && range.to > range.from ? `dal ${format(range.from, "d MMM", { locale: it })} al ${format(range.to, "d MMM", { locale: it })}` : format(range.from, "d MMM yyyy", { locale: it })) : ""}
          </Button>
        </Card>
        <Card className="border-slate-200 p-4">
          <p className="text-sm font-medium text-slate-800 mb-3">Chiusure programmate ({days.length})</p>
          {days.filter((k) => k >= new Date().toISOString().slice(0, 10)).length > 10 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mb-2" data-testid="chiusure-warning">Attenzione: molte chiusure future. I clienti non possono scegliere queste date, nemmeno con urgenza: la prima data disponibile slitta oltre l'ultima chiusura.</p>
          )}
          {days.length === 0 ? <p className="text-sm text-slate-400" data-testid="chiusure-empty">Nessuna chiusura inserita.</p> : (
            <ul className="space-y-1.5 max-h-80 overflow-y-auto" data-testid="chiusure-list">
              {days.map((k) => (
                <li key={k} data-testid="chiusura-row" className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-700 capitalize">
                  {fmt(k)}
                  <button type="button" onClick={() => remove(k)} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

export default function WorkSettingsPage() {
  return (
    <DashboardLayout title="Impostazioni Lavori" subtitle="Prezzi per cliente, tempistiche per tipo di lavoro e chiusure del laboratorio.">
      <Tabs defaultValue="prezzi" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="prezzi" data-testid="tab-prezzi"><Euro className="h-4 w-4 mr-1.5" /> Prezzi</TabsTrigger>
          <TabsTrigger value="tempistiche" data-testid="tab-tempistiche"><Clock className="h-4 w-4 mr-1.5" /> Tempistiche</TabsTrigger>
          <TabsTrigger value="chiusure" data-testid="tab-chiusure"><CalendarOff className="h-4 w-4 mr-1.5" /> Chiusure</TabsTrigger>
          <TabsTrigger value="collega" data-testid="tab-collega"><Euro className="h-4 w-4 mr-1.5" /> Listino collega</TabsTrigger>
          <TabsTrigger value="sicurezza" data-testid="tab-sicurezza"><Lock className="h-4 w-4 mr-1.5" /> Sicurezza report</TabsTrigger>
        </TabsList>
        <TabsContent value="prezzi"><PriceListPanel /></TabsContent>
        <TabsContent value="tempistiche"><TempistichePanel /></TabsContent>
        <TabsContent value="chiusure"><ChiusurePanel /></TabsContent>
        <TabsContent value="collega"><OperatorPricesPanel /></TabsContent>
        <TabsContent value="sicurezza"><ReportSecurityPanel /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
